import { z } from "zod";

export const ISO_CURRENCY_CODE_PATTERN = /^[A-Z]{3}$/;

export const COMMON_TRANSACTION_CURRENCY_CODES = ["USD", "CAD", "EUR", "GBP", "MXN"];

const SUPPORTED_ISO_CURRENCY_CODES = new Set([
  "AED",
  "AFN",
  "ALL",
  "AMD",
  "ANG",
  "AOA",
  "ARS",
  "AUD",
  "AWG",
  "AZN",
  "BAM",
  "BBD",
  "BDT",
  "BGN",
  "BHD",
  "BIF",
  "BMD",
  "BND",
  "BOB",
  "BOV",
  "BRL",
  "BSD",
  "BTN",
  "BWP",
  "BYN",
  "BZD",
  "CAD",
  "CDF",
  "CHE",
  "CHF",
  "CHW",
  "CLF",
  "CLP",
  "CNY",
  "COP",
  "COU",
  "CRC",
  "CUP",
  "CVE",
  "CZK",
  "DJF",
  "DKK",
  "DOP",
  "DZD",
  "EGP",
  "ERN",
  "ETB",
  "EUR",
  "FJD",
  "FKP",
  "GBP",
  "GEL",
  "GHS",
  "GIP",
  "GMD",
  "GNF",
  "GTQ",
  "GYD",
  "HKD",
  "HNL",
  "HTG",
  "HUF",
  "IDR",
  "ILS",
  "INR",
  "IQD",
  "IRR",
  "ISK",
  "JMD",
  "JOD",
  "JPY",
  "KES",
  "KGS",
  "KHR",
  "KMF",
  "KPW",
  "KRW",
  "KWD",
  "KYD",
  "KZT",
  "LAK",
  "LBP",
  "LKR",
  "LRD",
  "LSL",
  "LYD",
  "MAD",
  "MDL",
  "MGA",
  "MKD",
  "MMK",
  "MNT",
  "MOP",
  "MRU",
  "MUR",
  "MVR",
  "MWK",
  "MXN",
  "MXV",
  "MYR",
  "MZN",
  "NAD",
  "NGN",
  "NIO",
  "NOK",
  "NPR",
  "NZD",
  "OMR",
  "PAB",
  "PEN",
  "PGK",
  "PHP",
  "PKR",
  "PLN",
  "PYG",
  "QAR",
  "RON",
  "RSD",
  "RUB",
  "RWF",
  "SAR",
  "SBD",
  "SCR",
  "SDG",
  "SEK",
  "SGD",
  "SHP",
  "SLE",
  "SOS",
  "SRD",
  "SSP",
  "STN",
  "SVC",
  "SYP",
  "SZL",
  "THB",
  "TJS",
  "TMT",
  "TND",
  "TOP",
  "TRY",
  "TTD",
  "TWD",
  "TZS",
  "UAH",
  "UGX",
  "USD",
  "USN",
  "UYI",
  "UYU",
  "UYW",
  "UZS",
  "VED",
  "VES",
  "VND",
  "VUV",
  "WST",
  "XAF",
  "XAG",
  "XAU",
  "XBA",
  "XBB",
  "XBC",
  "XBD",
  "XCD",
  "XDR",
  "XOF",
  "XPD",
  "XPF",
  "XPT",
  "XSU",
  "XTS",
  "XUA",
  "XXX",
  "YER",
  "ZAR",
  "ZMW",
  "ZWG",
]);

export const normalizeCurrencyCode = (currencyCode: string): string => currencyCode.trim().toUpperCase();

export const isSupportedCurrencyCode = (currencyCode: string): boolean => {
  const normalized = normalizeCurrencyCode(currencyCode);
  return ISO_CURRENCY_CODE_PATTERN.test(normalized) && SUPPORTED_ISO_CURRENCY_CODES.has(normalized);
};

export const uniqueCurrencyCodes = (currencyCodes: ReadonlyArray<string | null | undefined>): string[] => {
  const normalizedCodes = currencyCodes
    .filter((code): code is string => typeof code === "string")
    .map(normalizeCurrencyCode)
    .filter(code => code.length > 0);

  return [...new Set(normalizedCodes)];
};

export const supportedCurrencyCodeSchema = z
  .string()
  .trim()
  .transform(normalizeCurrencyCode)
  .refine(isSupportedCurrencyCode, "Currency must be a valid ISO 4217 currency code.");

export const nullableSupportedCurrencyCodeSchema = z.preprocess(
  value => (value === "" ? null : value),
  supportedCurrencyCodeSchema.nullable(),
);
